import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Button,
	Callout,
	Card,
	Intent,
	NonIdealState,
	Spinner,
	Tag,
} from "@blueprintjs/core";
import Select from "react-select";
import clsx from "clsx";
import { IStore } from "redux/store";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";
import { IOption } from "redux/app/feedback/@types";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";

const selectClassNames = {
	option: ({ isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
		clsx(
			isFocused && optionStyles.focus,
			isSelected && optionStyles.selected,
			optionStyles.base
		),
	dropdownIndicator: () => dropdownIndicatorStyles,
	menu: () => menuStyles,
	clearIndicator: () => clearIndicatorStyles,
	placeholder: () => placeholderStyles,
	control: ({ isFocused, isDisabled }: { isFocused: boolean; isDisabled: boolean }) =>
		clsx(
			isFocused ? controlStyles.focus : controlStyles.nonFocus,
			isDisabled ? controlStyles.disabled : controlStyles.base
		),
	valueContainer: ({ isDisabled }: { isDisabled: boolean }) =>
		clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
};

type OwnProps = {
	/** Base URL of the lifecycle API; defaults to the portal's API base. */
	apiBase?: string;
	/** Acting user (audit trail display only; the API derives it from the token). */
	actor?: string;
};
type InstanceManagerProps = PropsFromRedux & OwnProps;

type Tenant = { id: string; name: string };
type Vessel = { id: string; name: string; imo: string };
type Instance = {
	slug: string;
	status: string;
	vesselName: string;
	imo: string;
	dbName: string;
	domain: string;
	backendImage: string;
	apiBaseUrl?: string | null;
	lastError?: string | null;
	tenantName?: string;
	updatedAt?: string;
};

const STATUS_INTENT: Record<string, Intent> = {
	running: Intent.SUCCESS,
	deploying: Intent.PRIMARY,
	stopped: Intent.WARNING,
	error: Intent.DANGER,
	destroyed: Intent.NONE,
};

function InstanceManager(props: InstanceManagerProps) {
	const apiBase = props.apiBase ?? commonUtils.getAPIBaseUrl();

	// TEMPORARY: Vessel Instances tool is restricted to a single email.
	const canAccessVesselInstances =
		props.currentUserEmail?.toLowerCase() === "abdul.ghani@volteo.com";

	const [tenants, setTenants] = useState<Tenant[]>([]);
	const [selectedTenantOption, setSelectedTenantOption] = useState<IOption | null>(
		null
	);
	const [vessels, setVessels] = useState<Vessel[]>([]);
	const [selectedVesselOption, setSelectedVesselOption] = useState<IOption | null>(
		null
	);
	const [loadingTenants, setLoadingTenants] = useState(false);
	const [loadingVessels, setLoadingVessels] = useState(false);
	const [instances, setInstances] = useState<Instance[]>([]);
	const [loadingInstances, setLoadingInstances] = useState(true);
	const [deploying, setDeploying] = useState(false);
	const [busySlug, setBusySlug] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const tenantId = selectedTenantOption?.value || "";
	const vesselId = selectedVesselOption?.value || "";

	const tenantOptions = useMemo(
		() =>
			tenants
				.map((tenant) => ({ label: tenant.name, value: tenant.id }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[tenants]
	);

	const vesselOptions = useMemo(
		() =>
			vessels
				.map((vessel) => ({
					label: vessel.imo
						? `${vessel.name} (IMO ${vessel.imo})`
						: vessel.name,
					value: vessel.id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[vessels]
	);

	const authedFetch = useCallback(
		async (path: string, init: RequestInit = {}) => {
			const response = await fetch(`${apiBase}${path}`, {
				...init,
				headers: {
					"Content-Type": "application/json",
					[authConstants.headers.TOKEN]: props.authToken || "",
					...(init.headers || {}),
				},
			});
			const body = await response.json().catch(() => null);
			if (!response.ok) {
				throw new Error(body?.message || `Request failed (${response.status})`);
			}
			return body;
		},
		[apiBase, props.authToken]
	);

	const refreshInstances = useCallback(async () => {
		try {
			const list: Instance[] = await authedFetch(`/api/instances`);
			setInstances(
				list.filter((instance) => instance.status !== "destroyed")
			);
		} catch (e: any) {
			setErrorMessage(e?.message || "Failed to load instances");
		} finally {
			setLoadingInstances(false);
		}
	}, [authedFetch]);

	useEffect(() => {
		if (!canAccessVesselInstances) {
			setLoadingInstances(false);
			return;
		}
		setLoadingTenants(true);
		authedFetch(`/api/tenants`)
			.then((docs: any[]) =>
				setTenants(docs.map((doc) => ({ id: doc._id, name: doc.name })))
			)
			.catch((e) => setErrorMessage(e?.message || "Failed to load tenants"))
			.finally(() => setLoadingTenants(false));
		refreshInstances();
	}, [authedFetch, refreshInstances, canAccessVesselInstances]);

	// Poll while a deploy is in flight so the table converges on its own.
	useEffect(() => {
		if (!canAccessVesselInstances) return;
		if (!instances.some((instance) => instance.status === "deploying")) return;
		const timer = setInterval(refreshInstances, 10_000);
		return () => clearInterval(timer);
	}, [instances, refreshInstances, canAccessVesselInstances]);

	useEffect(() => {
		if (!canAccessVesselInstances) return;
		setVessels([]);
		setSelectedVesselOption(null);
		if (!tenantId) return;
		setLoadingVessels(true);
		authedFetch(`/api/tenants/${encodeURIComponent(tenantId)}/vessels`)
			.then(setVessels)
			.catch((e) => setErrorMessage(e?.message || "Failed to load vessels"))
			.finally(() => setLoadingVessels(false));
	}, [tenantId, authedFetch, canAccessVesselInstances]);

	const selectedVessel = useMemo(
		() => vessels.find((vessel) => vessel.id === vesselId) || null,
		[vessels, vesselId]
	);

	if (!canAccessVesselInstances) {
		return (
			<div className="min-h-[calc(100vh-98px)] bg-gradient-to-br from-gray-50 to-gray-100 m-0 flex items-center justify-center">
				<NonIdealState
					icon="lock"
					title="Unauthorized"
					description="Vessel Instances is temporarily restricted."
				/>
			</div>
		);
	}

	const handleDeploy = async () => {
		if (!vesselId) return;
		setDeploying(true);
		setErrorMessage(null);
		try {
			await authedFetch(`/api/instances`, {
				method: "POST",
				body: JSON.stringify({ vesselId }),
			});
		} catch (e: any) {
			setErrorMessage(e?.message || "Deploy failed");
		} finally {
			setDeploying(false);
			refreshInstances();
		}
	};

	const runAction = async (slug: string, action: string, body?: object) => {
		setBusySlug(slug);
		setErrorMessage(null);
		try {
			if (action === "destroy") {
				await authedFetch(`/api/instances/${encodeURIComponent(slug)}`, {
					method: "DELETE",
				});
			} else {
				await authedFetch(
					`/api/instances/${encodeURIComponent(slug)}/${action}`,
					{ method: "POST", body: body ? JSON.stringify(body) : undefined }
				);
			}
		} catch (e: any) {
			setErrorMessage(e?.message || `${action} failed`);
		} finally {
			setBusySlug(null);
			refreshInstances();
		}
	};

	const handleUpdate = (slug: string) => {
		const version = window.prompt(
			"New backend image tag (e.g. 1.107.0). Leave empty to use the default tag."
		);
		if (version === null) return;
		const migrate = window.confirm("Run migrations on the new revision?");
		runAction(slug, "update", {
			version: version.trim() || undefined,
			migrate,
		});
	};

	const handleDestroy = (slug: string) => {
		if (
			!window.confirm(
				`Destroy instance '${slug}'? The Cloud Run service is deleted; the vessel's CouchDB data is NOT touched.`
			)
		) {
			return;
		}
		runAction(slug, "destroy");
	};

	const renderActions = (instance: Instance) => {
		const busy = busySlug === instance.slug;
		return (
			<div className="flex flex-wrap gap-1">
				{instance.status === "stopped" || instance.status === "error" ? (
					<Button
						small
						disabled={busy}
						icon="play"
						onClick={() => runAction(instance.slug, "start")}
					>
						Start
					</Button>
				) : (
					<>
						<Button
							small
							disabled={busy}
							icon="stop"
							onClick={() => runAction(instance.slug, "stop")}
						>
							Stop
						</Button>
						<Button
							small
							disabled={busy}
							icon="refresh"
							onClick={() => runAction(instance.slug, "restart")}
						>
							Restart
						</Button>
					</>
				)}
				<Button
					small
					disabled={busy}
					icon="automatic-updates"
					onClick={() => handleUpdate(instance.slug)}
				>
					Update
				</Button>
				<Button
					small
					disabled={busy}
					icon="trash"
					intent={Intent.DANGER}
					onClick={() => handleDestroy(instance.slug)}
				>
					Destroy
				</Button>
			</div>
		);
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gradient-to-br from-gray-50 to-gray-100 m-0">
			<div className="p-6 md:p-8 lg:p-12 max-w-[1600px] mx-auto space-y-8">
				<div>
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
						Vessel Instances
					</h1>
					<p className="text-gray-600 text-lg">
						Deploy and manage dedicated per-vessel instances.
					</p>
				</div>

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error">
						{errorMessage}
					</Callout>
				)}

				<Card elevation={1} className="bg-white">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">
						New instance
					</h3>
					<div className="flex flex-wrap items-end gap-4">
						<div className="w-72">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Tenant
							</label>
							<Select
								id="instance-tenants"
								isClearable
								isSearchable
								isLoading={loadingTenants}
								key={"tenants" + selectedTenantOption?.value}
								placeholder="Search tenants"
								unstyled
								value={selectedTenantOption}
								options={tenantOptions}
								onChange={(newValue) => {
									setSelectedTenantOption(newValue);
									setSelectedVesselOption(null);
								}}
								className="text-sm"
								menuPosition="fixed"
								classNames={selectClassNames}
							/>
						</div>
						<div className="w-72">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Vessel
							</label>
							<Select
								id="instance-vessels"
								isClearable
								isSearchable
								isLoading={loadingVessels}
								isDisabled={!selectedTenantOption}
								key={"vessels" + selectedTenantOption?.value}
								placeholder={
									selectedTenantOption
										? "Search vessels"
										: "Select a tenant first"
								}
								unstyled
								value={selectedVesselOption}
								options={vesselOptions}
								onChange={(newValue) => setSelectedVesselOption(newValue)}
								className="text-sm"
								menuPosition="fixed"
								classNames={selectClassNames}
								noOptionsMessage={() =>
									loadingVessels ? "Loading…" : "No vessels found"
								}
							/>
						</div>
						<div className="flex items-center gap-3 pb-0.5">
							<Button
								intent={Intent.PRIMARY}
								icon="cloud-upload"
								disabled={
									!vesselId ||
									deploying ||
									(selectedVessel != null && !selectedVessel.imo)
								}
								loading={deploying}
								onClick={handleDeploy}
							>
								Deploy instance
							</Button>
							{selectedVessel && !selectedVessel.imo && (
								<Tag intent={Intent.WARNING}>
									No IMO number — cannot deploy
								</Tag>
							)}
						</div>
					</div>
				</Card>

				<Card elevation={1} className="bg-white">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-lg font-semibold text-gray-900">Instances</h3>
						<Button
							small
							icon="refresh"
							disabled={loadingInstances}
							onClick={() => {
								setLoadingInstances(true);
								refreshInstances();
							}}
						>
							Refresh
						</Button>
					</div>

					{loadingInstances ? (
						<div className="py-10 flex justify-center">
							<Spinner size={36} />
						</div>
					) : instances.length === 0 ? (
						<NonIdealState
							icon="cloud"
							title="No instances"
							description="Deploy the first vessel instance above."
							className="py-10"
						/>
					) : (
						<div className="overflow-x-auto">
							<table className="min-w-full text-sm">
								<thead>
									<tr className="text-left text-gray-500 border-b border-gray-200">
										<th className="py-2 pr-4">Vessel</th>
										<th className="py-2 pr-4">Status</th>
										<th className="py-2 pr-4">Domain</th>
										<th className="py-2 pr-4">Database</th>
										<th className="py-2 pr-4">Image</th>
										<th className="py-2 pr-4">Actions</th>
									</tr>
								</thead>
								<tbody>
									{instances.map((instance) => (
										<tr
											key={instance.slug}
											className="border-b border-gray-100 align-top"
										>
											<td className="py-3 pr-4">
												<div className="font-medium text-gray-900">
													{instance.vesselName}
												</div>
												<div className="text-gray-500">
													{instance.tenantName || "—"}
													{instance.imo ? ` · IMO ${instance.imo}` : ""}
												</div>
											</td>
											<td className="py-3 pr-4">
												<Tag
													intent={STATUS_INTENT[instance.status] || Intent.NONE}
												>
													{instance.status}
												</Tag>
												{instance.status === "error" && instance.lastError && (
													<div className="text-xs text-red-600 mt-1 max-w-xs break-words">
														{instance.lastError}
													</div>
												)}
											</td>
											<td className="py-3 pr-4">
												{instance.status === "running" ? (
													<a
														href={`https://${instance.domain}`}
														target="_blank"
														rel="noreferrer"
														className="text-blue-600 hover:underline"
													>
														{instance.domain}
													</a>
												) : (
													<span className="text-gray-500">
														{instance.domain}
													</span>
												)}
											</td>
											<td className="py-3 pr-4 font-mono text-xs">
												{instance.dbName}
											</td>
											<td className="py-3 pr-4 font-mono text-xs max-w-[220px] break-all">
												{instance.backendImage}
											</td>
											<td className="py-3 pr-4">{renderActions(instance)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</Card>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {
		authToken: store.app.auth.authToken,
		currentUserEmail: (store.app.auth.user as any)?.email as string | undefined,
	};
}

const reduxConnector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(InstanceManager);
